import type { TankDTO } from "../../../shared/protocol/index.js";
import { state } from "../../state.js";
import type { Renderer } from "../Renderer.js";
import { drawWindupStubs } from "../scope.js";
import { shade } from "../util.js";
import { drawLocalIndicator } from "./indicator.js";

/** Battle City 8-bit theme. Called inside drawTank's save/translate/alpha.
 *  `treadPhase` is the signed distance (world px) rolled along the tank's own
 *  forward axis since last frame — scrolls the tread teeth in lock-step with
 *  real motion (forward one way, reverse the other, frozen when still). */
export function drawBattleCityTank(rd: Renderer, t: TankDTO, isLocal: boolean, nowMs: number, treadPhase: number): void {
  const { ctx } = rd;
  const r = rd.tankR;

  // Local-player facing indicator: a glowing sky-blue arrow ahead of your tank.
  if (isLocal && t.alive) {
    drawLocalIndicator(ctx, r, t.bodyAngle, nowMs);
  }

  // Hard 8-bit drop shadow: a rotated silhouette square, offset in world
  // space so the "light" stays consistent regardless of facing.
  if (t.alive) {
    ctx.save();
    ctx.translate(3, 3);
    ctx.rotate(t.bodyAngle);
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(-r * 1.05, -r * 1.05, r * 2.1, r * 2.1);
    ctx.restore();
  }

  ctx.save();
  ctx.rotate(t.bodyAngle);

  // Determine Battle City tank team colors
  let baseColor = "#b8b8b8"; // Enemy: light grey
  if (isLocal) {
    baseColor = "#f8b800"; // Local player: yellow
  } else if (t.team !== -1) {
    const localTeam = rd.displayedSnap?.tanks.find(tk => tk.id === state.playerId)?.team;
    if (t.team === localTeam) {
      baseColor = "#00a800"; // Teammate: green
    }
  }

  // Resolve the style for this tank:
  // Local player uses their selected custom style. Others are assigned one based on their ID hash.
  let style = state.retroStyle;
  if (!isLocal) {
    const hash = t.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const styles = ["basic", "fast", "heavy", "armored"] as const;
    style = styles[hash % styles.length];
  }

  let treadW = r * 0.35;
  let treadH = r * 2.2;
  let treadOffsetX = r * 0.9;

  if (style === "fast") {
    treadW = r * 0.28;
    treadH = r * 2.4;
    treadOffsetX = r * 0.85;
  } else if (style === "heavy") {
    treadW = r * 0.45;
    treadH = r * 2.0;
    treadOffsetX = r * 1.0;
  }

  // Draw treads (black)
  ctx.fillStyle = "#000000";
  ctx.fillRect(-treadH/2, -treadOffsetX - treadW/2, treadH, treadW);
  ctx.fillRect(-treadH/2, treadOffsetX - treadW/2, treadH, treadW);

  // Tread animation teeth (white dashes): scrolled by actual travel distance,
  // not the clock, so they roll forward/reverse with the tank and stop when
  // it's sitting still. Clipped to each fixed belt bar so the teeth roll off
  // the ends instead of the whole strip sliding (no sawtooth march).
  ctx.fillStyle = "#ffffff";
  const treadPitch = 6;
  const treadShift = ((treadPhase % treadPitch) + treadPitch) % treadPitch;
  for (const beltY of [-treadOffsetX - treadW/2, treadOffsetX - treadW/2]) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(-treadH/2, beltY, treadH, treadW);
    ctx.clip();
    for (let xOffset = -treadH/2 - treadPitch + treadShift; xOffset < treadH/2 + treadPitch; xOffset += treadPitch) {
      ctx.fillRect(xOffset, beltY + 1, 2, treadW - 2);
    }
    ctx.restore();
  }

  // Main body chassis and outlines based on style
  if (style === "fast") {
    // Sleeker, narrower chassis
    ctx.fillStyle = baseColor;
    ctx.fillRect(-r * 1.0, -r * 0.7, r * 2.0, r * 1.4);
    ctx.fillStyle = "#000000";
    ctx.strokeRect(-r * 1.0, -r * 0.7, r * 2.0, r * 1.4);
    // Hatch (pointing arrow)
    ctx.fillStyle = shade(baseColor, -25);
    ctx.beginPath();
    ctx.moveTo(r * 0.2, 0);
    ctx.lineTo(-r * 0.3, -r * 0.3);
    ctx.lineTo(-r * 0.3, r * 0.3);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#000000";
    ctx.stroke();
  } else if (style === "heavy") {
    // Wide, blocky chassis
    ctx.fillStyle = baseColor;
    ctx.fillRect(-r * 0.85, -r * 1.0, r * 1.7, r * 2.0);
    ctx.fillStyle = "#000000";
    ctx.strokeRect(-r * 0.85, -r * 1.0, r * 1.7, r * 2.0);
    // Double round hatches
    ctx.fillStyle = shade(baseColor, -25);
    ctx.beginPath();
    ctx.arc(-r * 0.2, -r * 0.3, r * 0.3, 0, Math.PI * 2);
    ctx.arc(-r * 0.2, r * 0.3, r * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.arc(-r * 0.2, -r * 0.3, r * 0.3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(-r * 0.2, r * 0.3, r * 0.3, 0, Math.PI * 2);
    ctx.stroke();
  } else if (style === "armored") {
    // Standard body but with extra armor plating
    ctx.fillStyle = baseColor;
    ctx.fillRect(-r * 0.85, -r * 0.85, r * 1.7, r * 1.7);
    ctx.fillStyle = "#000000";
    ctx.strokeRect(-r * 0.85, -r * 0.85, r * 1.7, r * 1.7);
    // Side armor panels
    ctx.fillStyle = shade(baseColor, -15);
    ctx.fillRect(-r * 0.85, -r * 0.85, r * 1.7, r * 0.2);
    ctx.fillRect(-r * 0.85, r * 0.65, r * 1.7, r * 0.2);
    ctx.fillStyle = "#000000";
    ctx.strokeRect(-r * 0.85, -r * 0.85, r * 1.7, r * 0.2);
    ctx.strokeRect(-r * 0.85, r * 0.65, r * 1.7, r * 0.2);
    // Octagonal hatch
    ctx.fillStyle = shade(baseColor, -25);
    const sz = r * 0.35;
    ctx.beginPath();
    ctx.moveTo(-sz * 0.5, -sz);
    ctx.lineTo(sz * 0.5, -sz);
    ctx.lineTo(sz, -sz * 0.5);
    ctx.lineTo(sz, sz * 0.5);
    ctx.lineTo(sz * 0.5, sz);
    ctx.lineTo(-sz * 0.5, sz);
    ctx.lineTo(-sz, sz * 0.5);
    ctx.lineTo(-sz, -sz * 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#000000";
    ctx.stroke();
  } else {
    // Basic: standard square body + center hatch
    ctx.fillStyle = baseColor;
    ctx.fillRect(-r * 0.85, -r * 0.85, r * 1.7, r * 1.7);
    ctx.fillStyle = "#000000";
    ctx.fillRect(-r * 0.85, -r * 0.85, r * 1.7, 2);
    ctx.fillRect(-r * 0.85, r * 0.85 - 2, r * 1.7, 2);
    ctx.fillRect(-r * 0.85, -r * 0.85, 2, r * 1.7);
    ctx.fillRect(r * 0.85 - 2, -r * 0.85, 2, r * 1.7);
    ctx.fillStyle = shade(baseColor, -25);
    ctx.fillRect(-r * 0.4, -r * 0.4, r * 0.8, r * 0.8);
    ctx.fillStyle = "#000000";
    ctx.strokeRect(-r * 0.4, -r * 0.4, r * 0.8, r * 0.8);
  }

  ctx.restore(); // End body rotation

  // Turret + barrel
  ctx.save();
  ctx.rotate(t.turretAngle);

  if (style === "heavy") {
    // Dual parallel barrels
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, -5.5, r * 1.4 + 1, 4);
    ctx.fillRect(0, 1.5, r * 1.4 + 1, 4);
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, -4.5, r * 1.4, 2);
    ctx.fillRect(0, 2.5, r * 1.4, 2);

    // Rectangular turret box
    ctx.fillStyle = "#000000";
    ctx.fillRect(-r * 0.5 - 1, -r * 0.55 - 1, r * 1.0 + 2, r * 1.1 + 2);
    ctx.fillStyle = shade(baseColor, 20);
    ctx.fillRect(-r * 0.5, -r * 0.55, r * 1.0, r * 1.1);
  } else if (style === "fast") {
    // Long thin barrel
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, -2.5, r * 1.8 + 1, 5);
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, -1, r * 1.8, 2);
    ctx.fillStyle = "#000000";
    ctx.fillRect(r * 1.8 - 2, -2.5, 2, 5);

    // Oval turret dome
    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 0.5 + 1, r * 0.4 + 1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = shade(baseColor, 20);
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 0.5, r * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
  } else if (style === "armored") {
    // Fat barrel + reinforced muzzle break
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, -4.5, r * 1.4 + 1, 9);
    ctx.fillRect(r * 1.4 - 3, -6, 5, 12);
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, -3, r * 1.4, 6);
    ctx.fillRect(r * 1.4 - 2, -4.5, 3, 9);

    // Octagonal turret dome
    ctx.fillStyle = "#000000";
    const tsz = r * 0.55;
    ctx.beginPath();
    ctx.moveTo(-tsz * 0.5 - 1, -tsz - 1);
    ctx.lineTo(tsz * 0.5 + 1, -tsz - 1);
    ctx.lineTo(tsz + 1, -tsz * 0.5 - 1);
    ctx.lineTo(tsz + 1, tsz * 0.5 + 1);
    ctx.lineTo(tsz * 0.5 + 1, tsz + 1);
    ctx.lineTo(-tsz * 0.5 - 1, tsz + 1);
    ctx.lineTo(-tsz - 1, tsz * 0.5 + 1);
    ctx.lineTo(-tsz - 1, -tsz * 0.5 - 1);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = shade(baseColor, 20);
    ctx.beginPath();
    ctx.moveTo(-tsz * 0.5, -tsz);
    ctx.lineTo(tsz * 0.5, -tsz);
    ctx.lineTo(tsz, -tsz * 0.5);
    ctx.lineTo(tsz, tsz * 0.5);
    ctx.lineTo(tsz * 0.5, tsz);
    ctx.lineTo(-tsz * 0.5, tsz);
    ctx.lineTo(-tsz, tsz * 0.5);
    ctx.closePath();
    ctx.fill();
  } else {
    // Basic: standard single barrel & round turret dome
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, -3.5, r * 1.5 + 1, 7);
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, -2, r * 1.5, 4);
    ctx.fillStyle = "#000000";
    ctx.fillRect(r * 1.5 - 2, -3.5, 2, 7);

    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.55 + 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = shade(baseColor, 20);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  // Laser windup
  if (t.charging && t.alive) {
    ctx.save();
    ctx.rotate(t.turretAngle);
    ctx.globalAlpha = 0.5 + 0.3 * Math.sin(nowMs / 50);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    drawWindupStubs(rd, ctx, r, t.weaponCharges);
    ctx.restore();
  }

  // Shield bubble: cyan circle
  if (t.shielded && t.alive) {
    ctx.save();
    const pulse = 0.85 + 0.15 * Math.sin(nowMs / 80);
    ctx.strokeStyle = "#00f8f8";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.45 * pulse, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();

  // Health bar
  if (t.alive && t.maxHp > 1) {
    const bw = r * 2;
    const bx = t.x - r;
    const by = t.y - r - 6;
    const frac = Math.max(0, Math.min(1, t.hp / t.maxHp));
    ctx.fillStyle = "rgba(255, 255, 255, 0.2)"; // Light backdrop
    ctx.fillRect(bx, by, bw, 3);
    ctx.fillStyle = frac > 0.5 ? "#00a800" : frac > 0.25 ? "#f8b800" : "#a83800";
    ctx.fillRect(bx, by, bw * frac, 3);
  }

  // Name / respawn label
  ctx.font = "bold 13px 'VT323', monospace";
  ctx.textAlign = "center";
  if (t.alive) {
    ctx.fillStyle = isLocal ? "#f8b800" : "#ffffff"; // Local is yellow, others white
    const above = t.y - r - (t.maxHp > 1 ? 13 : 8);
    const labelY = above < 10 ? t.y + r + 14 : above;
    ctx.fillText(t.name, t.x, labelY);
  } else {
    ctx.fillStyle = "#808080"; // Grey death timer
    ctx.fillText(`${Math.ceil(t.respawnIn)}`, t.x, t.y + 4);
  }
}
